import { getMonitoringConfig, type MonitoringConfig } from "./monitoring.functions";

let started = false;

type SentryModule = typeof import("@sentry/react");
let sentry: SentryModule | null = null;

/**
 * Boots Sentry in the browser only, using a DSN served by the backend.
 * Safe no-op when no DSN is configured (local/preview without the secret).
 */
export async function initMonitoring() {
  if (typeof window === "undefined" || started) return;
  started = true;

  let config: MonitoringConfig;
  try {
    config = await getMonitoringConfig();
  } catch {
    return;
  }
  if (!config.dsn) return;

  const Sentry = await import("@sentry/react");
  sentry = Sentry;

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release ?? undefined,
    tracesSampleRate: config.tracesSampleRate,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Turns console.error / console.warn output into breadcrumbs + captured events.
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
      Sentry.extraErrorDataIntegration(),
    ],
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "AbortError",
    ],
    beforeSend(event) {
      // Never ship auth tokens or query strings that may carry them.
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          url.search = "";
          event.request.url = url.toString();
        } catch {
          /* ignore */
        }
      }
      return event;
    },
  });
}

export function captureMonitoringError(error: unknown, context: Record<string, unknown> = {}) {
  sentry?.captureException(error, { extra: context });
}

export function setMonitoringUser(user: { id: string; role?: string } | null) {
  if (!sentry) return;
  sentry.setUser(user ? { id: user.id, ...(user.role ? { segment: user.role } : {}) } : null);
}
